import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

import AppUtils from "../../Utils/utils"
import { preferencesSuccess } from "../User/redux/user.actions"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getUsersFilterOptionsRequest: null,
  getUsersFilterOptionsSuccess: ["payload"], // payload: { data }
  getUsersFilterOptionsFailure: ["payload"], // payload: { data }

  getUsersRequest: ["payload"], // payload: { params }
  getUsersSuccess: ["payload"], // payload: { data }

  deleteUserRequest: ["payload"], // payload: { params }
  deleteUserSuccess: ["payload"], // payload: { data }

  giftRequest: ["payload"],
  giftSuccess: ["payload"],

  withdrawRequest: ["payload"],
  withdrawSuccess: ["payload"],

  switchToLoyalBillingRequest: ["payload"], // payload: { userId }
  switchToLoyalBillingSuccess: ["payload"],

  getUserInfoCommentRequest: ["payload"],
  getUserInfoCommentSuccess: ["payload"],

  getUserBusinessAccountsListRequest: ["payload"],
  getUserBusinessAccountsListSuccess: ["payload"],

  getUserCommentRequest: ["payload"],
  getUserCommentSuccess: ["payload"],

  saveCommentRequest: ["payload"],
  saveCommentSuccess: ["payload"],

  deleteUserCommentRequest: ["payload"],
  deleteUserCommentSuccess: ["payload"],

  deleteUserCommentAttachmentRequest: ["payload"],
  deleteUserCommentAttachmentSuccess: ["payload"],

  usersFailure: ["payload"], // payload: { errors }
  clearUserCommentsData: null,

  clearUsersFilters: null,
  usersSetProp: ["key", "value"],

  getUsersExportRequest: ["payload"], // payload: { params }
  getUsersExportSuccess: ["payload"], // payload: { url }
  getUsersExportFailure: ["payload"], // payload: { errors }

  logout: null
})

export const UsersTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "user_full_name", label: "User full name", active: false },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "created_timestamp", label: "Creation date", active: true },
  { field_id: "locked", label: "Locked", active: true },
  { field_id: "last_lock_at", label: "Last lock at", active: true },
  { field_id: "last_active", label: "Last activity", active: true },
  { field_id: "categories", label: "Type", active: true },
  { field_id: "balance_current", label: "Current Balance", active: true },
  { field_id: "alerts_in_debt_count", label: "Alerts in Debt", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "ba_status", label: "BA status", active: true },
  { field_id: "role", label: "Role", active: true },
  { field_id: "role_status", label: "Role status", active: true }
]

export const INITIAL_STATE = Immutable({
  fetching: false,
  saveFetching: false,
  switchToLoyalBillingFetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    status: "all",

    user_full_name: [],
    user_email: [],
    created_timestamp: null,
    last_active: null,

    balance_current: { min: null, max: null, type: null },
    alerts_in_debt_count: { min: null, max: null, type: null },

    business_account: [],
    ba_status: "all",
    role: "all",
    role_status: "all"
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    ba_status: [],
    role: [],
    role_status: []
  },

  visibleFiltersAdmin: { status: true, user_email: true, user_full_name: true },

  exportFetching: false,
  exportResult: null,

  saveResult: null,

  commentsFetching: false,
  userInfoComment: null,
  userCommentData: null,
  userBusinessAccountsList: []
})

/* ------------- Selectors ------------- */
export const selectAreFiltersDefault = (state) =>
  isEqual(state.users.filters, INITIAL_STATE.filters)

/* ------------- Functions for reducer cases ------------- */
const getUsersFilterOptionsRequest = (state) => state.merge({ fetchingFilterOptions: true })
const getUsersFilterOptionsSuccess = (state, { payload: filterOptions }) =>
  state.merge({
    fetchingFilterOptions: false,
    filterOptions: {
      ...filterOptions,
      ba_status: filterOptions?.["business_account_status"] || filterOptions?.["ba_status"]
    }
  })
const getUsersFilterOptionsFailure = (state) => state.merge({ fetchingFilterOptions: false })

const getUsersRequest = (state) => state.merge({ fetching: true })
const getUsersSuccess = (state, { payload: result }) => {
  if (result.results) {
    return state.merge({
      fetching: false,
      result: {
        ...result,
        results: result.results?.flatMap(({ business_accounts, ...item }) =>
          business_accounts?.length
            ? business_accounts?.map((account) => ({
                ...item,
                row_id: `${item?.id}_${account?.id}_${AppUtils.randomKey()}`,
                business_account: account
              }))
            : {
                ...item,
                row_id: `${item?.id}_${AppUtils.randomKey()}`,
                business_account: null
              }
        )
      }
    })
  } else {
    return state.merge({
      fetching: false,
      result: {
        count: result?.length || 0,
        results: result
      }
    })
  }
}

const deleteUserRequest = (state) => state.merge({ fetching: true })
const deleteUserSuccess = (state, { payload: deletedBA }) => {
  const newResults = [...state.result.results].filter(
    (el) => el.id !== (deletedBA?.id || deletedBA)
  )
  return state.merge({
    fetching: false,
    result: {
      count: state.result?.length - 1,
      results: newResults
    },
    saveResult: "user_delete"
  })
}

const usersFailure = (state) =>
  state.merge({
    fetching: false,
    saveFetching: false,
    switchToLoyalBillingFetching: false
  })

export const usersSetProp = (state, { key, value }) => {
  const obj = {}
  if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
    if (key === "filters") obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state?.[key], ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

export const clearUsersFilters = (state) => {
  return state.merge({ filters: INITIAL_STATE.filters })
}

const getUsersExportRequest = (state) => state.merge({ exportFetching: true })
const getUsersExportSuccess = (state, { payload: exportResult }) =>
  state.merge({
    exportFetching: false,
    exportResult
  })
const getUsersExportFailure = (state) => state.merge({ exportFetching: false })

const giftRequest = (state) => state.merge({ saveFetching: true })
const giftSuccess = (state, { payload: data }) =>
  state.merge({
    saveFetching: false,
    saveResult: { type: "giftRequest", data }
  })

const withdrawRequest = (state) => state.merge({ saveFetching: true })
const withdrawSuccess = (state, { payload: data }) =>
  state.merge({
    saveFetching: false,
    saveResult: { type: "withdrawRequest", data }
  })

const switchToLoyalBillingRequest = (state) => state.merge({ switchToLoyalBillingFetching: true })
const switchToLoyalBillingSuccess = (state) => state.merge({ switchToLoyalBillingFetching: false })

const getUserInfoCommentRequest = (state) => state.merge({ commentsFetching: true })
const getUserInfoCommentSuccess = (state, { payload: data }) =>
  state.merge({ commentsFetching: false, userInfoComment: data })

const getUserBusinessAccountsListRequest = (state) => state.merge({ commentsFetching: true })
const getUserBusinessAccountsListSuccess = (state, { payload: data }) =>
  state.merge({
    commentsFetching: false,
    userBusinessAccountsList: data
  })

const getUserCommentRequest = (state) => state.merge({ commentsFetching: true })
const getUserCommentSuccess = (state, { payload: data }) =>
  state.merge({ commentsFetching: false, userCommentData: data })

const saveCommentRequest = (state) => state.merge({ commentsFetching: true })
const saveCommentSuccess = (state, { payload: data }) =>
  state.merge({ commentsFetching: false, userCommentData: data })

const deleteUserCommentRequest = (state) => state.merge({ commentsFetching: true })
const deleteUserCommentSuccess = (state, { payload: data }) =>
  state.merge({
    commentsFetching: false,
    userCommentData: { ...state.userCommentData, comment_text: null }
  })

const deleteUserCommentAttachmentRequest = (state) => state.merge({ commentsFetching: true })
const deleteUserCommentAttachmentSuccess = (state, { payload: data }) =>
  state.merge({
    commentsFetching: false
  })

const clearUserCommentsData = (state) =>
  state.merge({
    userInfoComment: null,
    userCommentData: null,
    userBusinessAccountsList: []
  })

const logout = (state) => {
  return state.merge({
    ...INITIAL_STATE
  })
}

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const USERS_PREFERENCES_VERSION = 1

const applyPreferences = (state, { payload }) => {
  const preference = payload?.["admin-customer-users"]?.data
  if (!preference || preference.version !== USERS_PREFERENCES_VERSION) return state
  if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
    return state

  return state.merge({
    filters: preference.filters,
    columnsAdmin: preference.columnsAdmin,
    visibleFiltersAdmin: preference.visibleFiltersAdmin
  })
}

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_USERS_FILTER_OPTIONS_REQUEST]: getUsersFilterOptionsRequest,
  [Types.GET_USERS_FILTER_OPTIONS_SUCCESS]: getUsersFilterOptionsSuccess,
  [Types.GET_USERS_FILTER_OPTIONS_FAILURE]: getUsersFilterOptionsFailure,

  [Types.GET_USERS_REQUEST]: getUsersRequest,
  [Types.GET_USERS_SUCCESS]: getUsersSuccess,

  [Types.GIFT_REQUEST]: giftRequest,
  [Types.GIFT_SUCCESS]: giftSuccess,

  [Types.WITHDRAW_REQUEST]: withdrawRequest,
  [Types.WITHDRAW_SUCCESS]: withdrawSuccess,

  [Types.SWITCH_TO_LOYAL_BILLING_REQUEST]: switchToLoyalBillingRequest,
  [Types.SWITCH_TO_LOYAL_BILLING_SUCCESS]: switchToLoyalBillingSuccess,

  [Types.DELETE_USER_REQUEST]: deleteUserRequest,
  [Types.DELETE_USER_SUCCESS]: deleteUserSuccess,

  [Types.GET_USER_INFO_COMMENT_REQUEST]: getUserInfoCommentRequest,
  [Types.GET_USER_INFO_COMMENT_SUCCESS]: getUserInfoCommentSuccess,

  [Types.GET_USER_BUSINESS_ACCOUNTS_LIST_REQUEST]: getUserBusinessAccountsListRequest,
  [Types.GET_USER_BUSINESS_ACCOUNTS_LIST_SUCCESS]: getUserBusinessAccountsListSuccess,

  [Types.GET_USER_COMMENT_REQUEST]: getUserCommentRequest,
  [Types.GET_USER_COMMENT_SUCCESS]: getUserCommentSuccess,

  [Types.SAVE_COMMENT_REQUEST]: saveCommentRequest,
  [Types.SAVE_COMMENT_SUCCESS]: saveCommentSuccess,

  [Types.DELETE_USER_COMMENT_REQUEST]: deleteUserCommentRequest,
  [Types.DELETE_USER_COMMENT_SUCCESS]: deleteUserCommentSuccess,

  [Types.DELETE_USER_COMMENT_ATTACHMENT_REQUEST]: deleteUserCommentAttachmentRequest,
  [Types.DELETE_USER_COMMENT_ATTACHMENT_SUCCESS]: deleteUserCommentAttachmentSuccess,

  [Types.USERS_FAILURE]: usersFailure,
  [Types.CLEAR_USER_COMMENTS_DATA]: clearUserCommentsData,

  [Types.CLEAR_USERS_FILTERS]: clearUsersFilters,

  [Types.USERS_SET_PROP]: usersSetProp,

  [Types.GET_USERS_EXPORT_REQUEST]: getUsersExportRequest,
  [Types.GET_USERS_EXPORT_SUCCESS]: getUsersExportSuccess,
  [Types.GET_USERS_EXPORT_FAILURE]: getUsersExportFailure,

  [Types.LOGOUT]: logout,

  [preferencesSuccess.type]: applyPreferences
})
